import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Phone, Copy, MapPin, Globe, Mail } from 'lucide-react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Spinner } from '@/shared/components/ui/Spinner';
import { Button } from '@/shared/components/ui/Button';
import { cn } from '@/shared/utils/utils';
import { useLeadDetail, useLeadCalls, useLeadMutations } from '../hooks/useLeads';
import { STATUSES, STATUS_LABELS, SUB_STATUSES, DISPOSITION_LABELS } from '../constants';
import { ActivityTimeline } from '@/features/activity/components/ActivityTimeline';
import { useEntityActivity } from '@/features/activity/hooks/useActivity';
import type { Lead } from '../types';

interface LeadDrawerProps {
  leadId: string | null;
  onClose: () => void;
}

type EditState = {
  status: string;
  sub_status: string;
  follow_up_date: string; // YYYY-MM-DD
  notes: string;
};

function ReadOnlyField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <label className="mb-1 block text-body-sm font-medium text-on-surface-variant">{label}</label>
      <div className="rounded-card-sm border border-outline bg-surface-container-low px-3 py-2 text-data-md text-on-surface">
        {value && String(value).trim() ? value : <span className="text-on-surface-variant/50">—</span>}
      </div>
    </div>
  );
}

/** Quick-action button — backend-real actions only (links + clipboard). */
function QuickAction({ icon, label, onClick, href }: { icon: React.ReactNode; label: string; onClick?: () => void; href?: string }) {
  const cls =
    'inline-flex items-center gap-1.5 rounded-card-sm border border-outline bg-surface px-2.5 py-1.5 text-body-sm font-medium text-on-surface-variant transition-colors hover:bg-surface-variant hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40';
  if (href) {
    return (
      <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className={cls}>
        {icon} {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {icon} {label}
    </button>
  );
}

export function LeadDrawer({ leadId, onClose }: LeadDrawerProps) {
  const { data: lead, isLoading } = useLeadDetail(leadId);
  const { updateLead, updateSubStatus, updateFollowUp } = useLeadMutations();
  const { data: calls, isLoading: callsLoading } = useLeadCalls(leadId);
  const { data: activity = [], isLoading: activityLoading, isError: activityError } = useEntityActivity('lead', leadId);
  const [activeTab, setActiveTab] = useState<'details' | 'calls' | 'activity'>('details');

  const [form, setForm] = useState<EditState>({ status: 'new', sub_status: '', follow_up_date: '', notes: '' });

  useEffect(() => {
    if (lead) {
      setForm({
        status: lead.status || 'new',
        sub_status: lead.sub_status || '',
        follow_up_date: (lead.follow_up_date || '').slice(0, 10),
        notes: lead.notes || '',
      });
      setActiveTab('details');
    }
  }, [lead]);

  const set = <K extends keyof EditState>(key: K, value: EditState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const saving = updateLead.isPending || updateSubStatus.isPending || updateFollowUp.isPending;
  const subStatusOptions = SUB_STATUSES[form.status] || [];

  const copy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !lead) return;
    try {
      await updateLead.mutateAsync({ id: leadId, payload: { status: form.status as Lead['status'], notes: form.notes } });
      if (form.sub_status !== (lead.sub_status || '')) {
        await updateSubStatus.mutateAsync({ id: leadId, sub_status: form.sub_status });
      }
      const originalFollowUp = (lead.follow_up_date || '').slice(0, 10);
      if (form.follow_up_date !== originalFollowUp) {
        await updateFollowUp.mutateAsync({ id: leadId, follow_up_date: form.follow_up_date || null });
      }
      toast.success('Lead updated successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.message || 'Failed to update lead');
    }
  };

  if (!leadId) return null;

  return (
    <Drawer open={!!leadId} onClose={onClose} title={lead ? lead.name : 'Loading…'} width="w-[520px]">
      {isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Spinner />
        </div>
      ) : !lead ? (
        <div className="flex h-32 items-center justify-center text-center text-on-surface-variant">
          This lead could not be loaded. You may not have access to it.
        </div>
      ) : (
        <div className="flex h-full flex-col">
          {/* Quick actions — backend-real only */}
          <div className="mb-4 flex flex-wrap gap-2">
            {lead.phone && <QuickAction icon={<Phone className="h-4 w-4" />} label="Call" href={`tel:${lead.phone}`} />}
            {lead.phone && <QuickAction icon={<Copy className="h-4 w-4" />} label="Copy phone" onClick={() => copy(lead.phone!, 'Phone')} />}
            {lead.emails?.[0] && <QuickAction icon={<Mail className="h-4 w-4" />} label="Copy email" onClick={() => copy(lead.emails![0], 'Email')} />}
            {lead.maps_url && <QuickAction icon={<MapPin className="h-4 w-4" />} label="Maps" href={lead.maps_url} />}
            {lead.website && <QuickAction icon={<Globe className="h-4 w-4" />} label="Website" href={lead.website} />}
          </div>

          {/* Tabs — only those backed by real data */}
          <div className="mb-4 flex gap-4 border-b border-outline px-1">
            {(['details', 'calls', 'activity'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'border-b-2 py-2 text-data-md font-semibold capitalize transition-colors',
                  activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-on-surface-variant hover:text-on-surface',
                )}
              >
                {tab === 'calls' ? 'Call History' : tab === 'activity' ? 'Activity' : 'Details'}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto pr-1">
            {activeTab === 'details' && (
              <form id="lead-drawer-form" onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <ReadOnlyField label="Name" value={lead.name} />
                  <ReadOnlyField label="Phone" value={lead.phone} />
                  <ReadOnlyField label="Website" value={lead.website} />
                  <ReadOnlyField label="Rating" value={lead.rating} />
                  <ReadOnlyField label="Niche" value={lead.niche} />
                  <ReadOnlyField label="Location" value={lead.location} />
                </div>

                <div className="h-px bg-outline" />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-body-sm font-medium text-on-surface">Status</label>
                    <select
                      value={form.status}
                      onChange={(e) => {
                        const next = e.target.value;
                        setForm((prev) => ({
                          ...prev,
                          status: next,
                          sub_status: (SUB_STATUSES[next] || []).includes(prev.sub_status) ? prev.sub_status : '',
                        }));
                      }}
                      className="w-full rounded-card-sm border border-outline bg-surface px-3 py-2 text-data-md capitalize text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s} className="capitalize">
                          {STATUS_LABELS[s]}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-body-sm font-medium text-on-surface">Sub-Status</label>
                    <select
                      value={form.sub_status}
                      onChange={(e) => set('sub_status', e.target.value)}
                      disabled={subStatusOptions.length === 0}
                      className="w-full rounded-card-sm border border-outline bg-surface px-3 py-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">— None —</option>
                      {subStatusOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-body-sm font-medium text-on-surface">Follow-up Date</label>
                    <input
                      type="date"
                      value={form.follow_up_date}
                      onChange={(e) => set('follow_up_date', e.target.value)}
                      className="w-full rounded-card-sm border border-outline bg-surface px-3 py-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-body-sm font-medium text-on-surface">Notes</label>
                  <textarea
                    rows={4}
                    value={form.notes}
                    onChange={(e) => set('notes', e.target.value)}
                    className="w-full rounded-card-sm border border-outline bg-surface px-3 py-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>

                <div className="flex justify-end pt-2">
                  <Button type="submit" loading={saving}>
                    Save Changes
                  </Button>
                </div>
              </form>
            )}

            {activeTab === 'calls' && (
              <div className="space-y-3">
                {callsLoading ? (
                  <div className="flex h-24 items-center justify-center">
                    <Spinner />
                  </div>
                ) : !calls || calls.length === 0 ? (
                  <div className="flex h-32 flex-col items-center justify-center rounded-card-sm border border-dashed border-outline bg-surface p-4 text-center text-on-surface-variant">
                    No calls logged for this lead yet.
                  </div>
                ) : (
                  calls.map((call) => (
                    <div key={call.id} className="rounded-card-sm border border-outline bg-surface p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-data-md font-semibold text-on-surface">
                          {DISPOSITION_LABELS[call.disposition] || call.disposition}
                        </span>
                        <span className="text-body-sm text-on-surface-variant">
                          {new Date(call.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-body-sm text-on-surface-variant">
                        {call.duration_sec > 0 && <span>{call.duration_sec}s</span>}
                        {call.agent_name && <span>· {call.agent_name}</span>}
                      </div>
                      {call.notes && <p className="mt-2 text-body-sm text-on-surface-variant">{call.notes}</p>}
                      {call.recording_file && (
                        <audio
                          controls
                          preload="none"
                          src={`${import.meta.env.VITE_API_BASE_URL ?? ''}/api/recordings/${call.recording_file}`}
                          className="mt-2 w-full"
                        />
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'activity' && (
              <ActivityTimeline
                items={activity}
                isLoading={activityLoading}
                isError={activityError}
                showActor
                emptyTitle="No activity yet"
                emptyDescription="Status changes, follow-ups, calls and comments will appear here."
              />
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}
